import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import "./Hero.css";
import { MyContext } from "../../Context/MyContext";
import { resolveImage, DEFAULT_CAR_IMAGE } from "../../../Utils/imageUtils";
import heroFallback from "../../../Assets/hero-banner-bg.png";
import { FaCar, FaChevronDown } from "react-icons/fa";

const SLIDE_MS = 6000;
const FADE_MS = 700;

function HeroSlideImage({ slide, index, isActive }) {
  const primary = resolveImage(slide?.image, "");
  const [src, setSrc] = useState(primary || heroFallback);

  useEffect(() => {
    setSrc(primary || heroFallback);
  }, [primary, slide?.id]);

  const handleError = () => {
    setSrc((current) => {
      if (current === heroFallback) return DEFAULT_CAR_IMAGE || heroFallback;
      return heroFallback;
    });
  };

  return (
    <img
      src={src}
      alt=""
      className="hero-slide-photo"
      loading={index <= 1 ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={index === 0 ? "high" : "auto"}
      referrerPolicy="no-referrer"
      onError={handleError}
      style={{ visibility: isActive ? "visible" : "hidden" }}
    />
  );
}

export default function Hero() {
  const { brands, carousel, fuel_type } = useContext(MyContext);

  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showBrands, setShowBrands] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [copyKey, setCopyKey] = useState(0);

  const sectionRef = useRef(null);
  const bottomRef = useRef(null);
  const transitionTimer = useRef(null);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const slides = carousel.length ? carousel : [];

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  const mediaScale = useTransform(scrollYProgress, [0, 0.35, 1], [1, 1.08, 1.24]);
  const mediaY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.58], [1, 0]);
  const copyY = useTransform(scrollYProgress, [0, 0.58], [0, -80]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0.28]);
  const darkenOpacity = useTransform(scrollYProgress, [0, 0.88], [0, 0.55]);
  const controlsOpacity = useTransform(scrollYProgress, [0, 0.42], [1, 0]);
  const cueOpacity = useTransform(scrollYProgress, [0, 0.38], [1, 0]);

  const goToSlide = useCallback(
    (index) => {
      if (!slides.length || isTransitioning) return;
      const next = (index + slides.length) % slides.length;
      if (next === current) return;

      setIsTransitioning(true);
      setCurrent(next);
      setCopyKey((k) => k + 1);
      transitionTimer.current = setTimeout(() => {
        setIsTransitioning(false);
      }, FADE_MS);
    },
    [slides.length, isTransitioning, current]
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(window.innerWidth >= 1024 ? 7 : 5);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!slides.length) return undefined;

    const interval = setInterval(() => {
      goToSlide(current + 1);
    }, SLIDE_MS);

    return () => clearInterval(interval);
  }, [slides.length, current, goToSlide]);

  useEffect(() => {
    return () => {
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
    };
  }, []);

  useEffect(() => {
    slides.forEach((slide) => {
      const src = resolveImage(slide.image, "");
      if (src) {
        const img = new Image();
        img.referrerPolicy = "no-referrer";
        img.src = src;
      }
    });
  }, [slides]);

  const handleBrandClick = (id) => {
    navigate(`/cars?brand=${id}`);
    setShowBrands(false);
  };

  const handleFuelClick = (id) => {
    navigate(`/cars?fuel=${id}`);
  };

  const scrollToExplore = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const activeSlide = slides[current] || {};

  const MediaWrapper = reduceMotion ? "div" : motion.div;
  const OverlayWrapper = reduceMotion ? "div" : motion.div;
  const CopyWrapper = reduceMotion ? "div" : motion.div;

  const mediaStyle = reduceMotion
    ? undefined
    : { scale: mediaScale, y: mediaY, transformOrigin: "center center" };

  const overlayStyle = reduceMotion ? undefined : { opacity: overlayOpacity };

  const copyStyle = reduceMotion
    ? undefined
    : { opacity: copyOpacity, y: copyY };

  return (
    <section
      className={`hero-section${reduceMotion ? "" : " hero-section--scroll"}`}
      ref={sectionRef}
    >
      <div className={reduceMotion ? undefined : "hero-scroll-track"}>
        <div className={reduceMotion ? undefined : "hero-sticky"}>
          <div className="hero">
            <MediaWrapper className="hero-media" style={mediaStyle}>
              {slides.map((slide, index) => (
                <div
                  key={slide.id || index}
                  className={`hero-slide ${index === current ? "is-active" : ""}`}
                  aria-hidden={index !== current}
                  style={{
                    backgroundImage: `url(${resolveImage(slide.image, heroFallback)})`,
                  }}
                >
                  <HeroSlideImage slide={slide} index={index} isActive={index === current} />
                </div>
              ))}
            </MediaWrapper>

            {!reduceMotion && (
              <motion.div
                className="hero-scroll-darken"
                style={{ opacity: darkenOpacity }}
                aria-hidden
              />
            )}

            <OverlayWrapper className="overlay" style={overlayStyle}>
              <CopyWrapper key={copyKey} className="hero-banner-copy" style={copyStyle}>
                <span className="hero-kicker hero-reveal hero-reveal--1">
                  <FaCar aria-hidden />
                  {activeSlide.kicker || "India's trusted used car marketplace"}
                </span>
                <h1 className="hero-reveal hero-reveal--2">
                  {activeSlide.text || "Find your perfect pre-owned car."}
                </h1>
                <p className="hero-reveal hero-reveal--3">
                  {activeSlide.subtext ||
                    "Browse inspected cars, compare prices, and drive home with confidence."}
                </p>
                <div className="hero-journey-steps hero-reveal hero-reveal--4" aria-hidden>
                  <span className={current >= 0 ? "is-lit" : ""}>Discover</span>
                  <span className="hero-journey-dot" />
                  <span className={current >= 1 ? "is-lit" : ""}>Compare</span>
                  <span className="hero-journey-dot" />
                  <span className={current >= 2 ? "is-lit" : ""}>Drive</span>
                </div>
              </CopyWrapper>
            </OverlayWrapper>

            {slides.length > 1 && (
              <motion.div
                className="hero-slider-controls hero-reveal hero-reveal--5"
                aria-label="Hero slideshow controls"
                style={reduceMotion ? undefined : { opacity: controlsOpacity }}
              >
                <div className="hero-dots">
                  {slides.map((slide, index) => (
                    <button
                      key={slide.id || index}
                      type="button"
                      className={index === current ? "active" : ""}
                      onClick={() => goToSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                      aria-current={index === current ? "true" : undefined}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            <motion.button
              type="button"
              className="hero-scroll-cue"
              onClick={scrollToExplore}
              aria-label="Scroll to explore brands"
              style={reduceMotion ? undefined : { opacity: cueOpacity }}
            >
              <span>Scroll to explore</span>
              <FaChevronDown className="hero-scroll-cue-icon" aria-hidden />
            </motion.button>

            <div className="hero-progress" aria-hidden>
              <span
                className="hero-progress-bar"
                style={{
                  width: `${((current + 1) / Math.max(slides.length, 1)) * 100}%`,
                }}
              />
            </div>
          </div>

          <div ref={bottomRef} className="hero-bottom-box">
            <div className="hero-bottom-head">
              <h2>Shop by brand</h2>
              <button type="button" onClick={() => navigate("/cars")}>
                View all cars
              </button>
            </div>

            <div className="brand-section">
              <div className="brand-list">
                {brands.slice(0, visibleCount).map((brand) => (
                  <div
                    key={brand.id}
                    className="brand-card"
                    onClick={() => handleBrandClick(brand.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleBrandClick(brand.id)}
                  >
                    <img src={resolveImage(brand.image)} alt={brand.name} />
                    <p>{brand.name}</p>
                  </div>
                ))}

                <div
                  className="brand-card more-card"
                  onClick={() => setShowBrands(true)}
                  role="button"
                  tabIndex={0}
                >
                  <p>More -&gt;</p>
                </div>
              </div>
            </div>

            <div className="fuel-type">
              <span className="fuel-type-label">Fuel type</span>
              {(fuel_type || []).map((fuel) => (
                <button
                  key={fuel.id}
                  type="button"
                  className="fuel-btn"
                  onClick={() => handleFuelClick(fuel.id)}
                >
                  {fuel.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showBrands && (
        <div className="brand-popup-overlay">
          <div className="brand-popup-card">
            <div className="popup-header">
              <h3>All Brands</h3>
              <button type="button" className="popup-close" onClick={() => setShowBrands(false)}>
                X
              </button>
            </div>

            <div className="popup-brand-list">
              {brands.map((brand) => (
                <div
                  key={brand.id}
                  className="popup-brand-card"
                  onClick={() => handleBrandClick(brand.id)}
                >
                  <img src={resolveImage(brand.image)} alt={brand.name} />
                  <p>{brand.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
